// Import dynamically to avoid TypeScript errors
import AcrossTabsModule, { MessageData } from 'across-tabs'

interface TabInfo {
  id: string
  name: string
  url: string
  ref: Window
  status: 'open' | 'close'
  createdOn: number
  updatedOn: number
  windowName?: string
}

/**
 * TabManager - Singleton class to manage tabs across the application
 * Ensures only one parent instance is created and properly manages child tabs
 */
class TabManager {
  private static instance: TabManager
  private parent: typeof AcrossTabsModule.Parent.prototype
  private tabRegistry: Map<
    string,
    {
      ref: { ref: Window | null } | Window | null
      isOpen: boolean
      tabInfo?: {
        id: string
        windowName: string
      }
    }
  > = new Map()
  private callbacks: Map<string, (data: MessageData) => void> = new Map()

  private constructor() {
    this.parent = new AcrossTabsModule.Parent({
      heartBeatInterval: 1000,
      removeClosedTabs: true, // Let the library help with cleanup
      shouldInitImmediately: true,
      enablePolling: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onHandshakeCallback: (data: any) => {
        console.log('[TabManager] New tab connected (safe props):', { id: data.id, windowName: data.windowName })

        // Update tab registry when a new tab connects
        if (data.windowName) {
          this.tabRegistry.set(data.windowName, {
            ref: data.ref,
            isOpen: true,
            tabInfo: {
              id: data.id,
              windowName: data.windowName,
            },
          })
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChildCommunication: (data: any) => {
        console.log('[TabManager] Received message from child:', data)

        if (data && data.type === 'FOCUS_PARENT') {
          // Use setTimeout to increase likelihood of focus succeeding
          setTimeout(() => {
            console.log('[TabManager] Attempting to focus parent window...')
            window.focus()
          }, 1000)
        } else if (data && data.type === 'CHILD_CLOSED') {
          // Handle child explicitly telling us it's closing
          if (data.windowName) {
            this.markTabAsClosed(data.windowName)
          }
        } else if (data) {
          // Ensure data exists before dispatching
          // Dispatch message to registered callbacks
          this.callbacks.forEach((callback) => callback(data))
        }
      },
      // Track when polling detects a closed tab
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onPollingCallback: (data: any) => {
        // Fix: Add null check for data before accessing properties
        if (data && data.type === 'TABS_UPDATED') {
          // Sync our registry with what the library knows
          this.syncTabRegistry()
        }
      },
    })

    // Initialize tab registry with any tabs that might already be open
    this.syncTabRegistry()
  }

  public static getInstance(): TabManager {
    if (!TabManager.instance) {
      TabManager.instance = new TabManager()
    }
    return TabManager.instance
  }

  /**
   * Synchronize our tab registry with what across-tabs library knows
   */
  private syncTabRegistry(): void {
    try {
      const openTabs = this.parent.getOpenedTabs()
      const closedTabs = this.parent.getClosedTabs()

      // Mark tabs as closed if they're in the closed tabs list
      closedTabs.forEach((tab) => {
        if (tab && tab.windowName) {
          this.markTabAsClosed(tab.windowName)
        }
      })

      // Update registry with open tabs
      openTabs.forEach((tab) => {
        if (tab && tab.windowName) {
          this.tabRegistry.set(tab.windowName, {
            ref: tab.ref,
            isOpen: true,
            tabInfo: {
              id: tab.id,
              windowName: tab.windowName,
            },
          })
        }
      })
    } catch (err) {
      console.error('[TabManager] Error syncing tab registry:', err)
    }
  }

  /**
   * Mark a tab as closed in our registry
   */
  private markTabAsClosed(windowName: string): void {
    const tabEntry = this.tabRegistry.get(windowName)
    if (tabEntry) {
      tabEntry.isOpen = false
      tabEntry.ref = null
      this.tabRegistry.set(windowName, tabEntry)
    }
  }

  /**
   * Register a callback to be executed when child tab sends a message
   * @param key Unique identifier for this callback
   * @param callback Function to be called with message data
   */
  public registerCallback(key: string, callback: (data: MessageData) => void): void {
    this.callbacks.set(key, callback)
  }

  /**
   * Unregister a previously registered callback
   * @param key Identifier of the callback to remove
   */
  public unregisterCallback(key: string): void {
    this.callbacks.delete(key)
  }

  /**
   * Check if a tab with the given logical name exists and is actually open
   * @param logicalName Logical identifier for the window
   * @returns True if the tab exists and is open, false otherwise
   */
  public isTabOpen(logicalName: string): boolean {
    // First check our local registry
    const tabEntry = this.tabRegistry.get(logicalName)
    if (!tabEntry || !tabEntry.isOpen) {
      return false
    }

    try {
      // Double-check with the parent's knowledge
      const allTabs = this.parent.getAllTabs()
      const matchingTab = allTabs.find((tab) => tab && tab.windowName === logicalName && tab.status === 'open')

      // If the tab is found in the parent's list and has an accessible reference, it's open
      if (matchingTab && matchingTab.ref) {
        try {
          // Try to access a property to see if the window is actually accessible
          // This will throw an error if the window has been closed
          if (matchingTab.ref.closed === false) {
            return true
          }
        } catch {
          // Window reference is no longer valid
          this.markTabAsClosed(logicalName)
          return false
        }
      }

      // Either not found in parent's list or reference is not accessible
      if (tabEntry.isOpen) {
        // Update our registry if we thought it was open but it's not
        this.markTabAsClosed(logicalName)
      }
    } catch (err) {
      console.error('[TabManager] Error checking if tab is open:', err)
      // If we can't determine for sure, default to closed
      if (tabEntry.isOpen) {
        this.markTabAsClosed(logicalName)
      }
      return false
    }

    return false
  }

  /**
   * Opens a new window with specific dimensions and a stable logical name
   * @param url URL to open in the new window
   * @param logicalName Logical identifier for the window
   * @returns Reference to the child window
   */
  public openNewWindowWithDimensions(url: string, logicalName: string): Window | null {
    // First check if a tab with this name is already open
    if (this.isTabOpen(logicalName)) {
      console.log(`[TabManager] Tab ${logicalName} is already open, focusing instead of opening new`)
      return this.focusExistingTab(logicalName)
    }

    const screenWidth = window.screen.width
    const screenHeight = window.screen.height

    const parentWidth = Math.floor(screenWidth * 0.6)
    const childWidth = Math.floor(screenWidth * 0.4)
    const childHeight = screenHeight

    const childLeft = parentWidth
    const childTop = 0

    // Use logicalName directly as the window name
    const windowName = logicalName
    console.log(`[TabManager] Opening window: name=${windowName}, url=${url.substring(0, 100)}...`)

    // Store a placeholder in registry before opening to prevent race conditions
    this.tabRegistry.set(windowName, {
      ref: null,
      isOpen: false, // Will be updated on handshake
      tabInfo: {
        id: '',
        windowName: windowName,
      },
    })

    const childWindow = this.parent.openNewTab({
      url: url,
      windowName: windowName,
      windowFeatures: `width=${childWidth},height=${childHeight},left=${childLeft},top=${childTop}`,
    })

    // Update registry with the new window reference
    if (childWindow) {
      this.tabRegistry.set(windowName, {
        ref: childWindow,
        isOpen: true,
        tabInfo: {
          id: '', // Will be updated on handshake
          windowName: windowName,
        },
      })
    }

    return childWindow
  }

  /**
   * Focus an existing tab if it's open
   * @param logicalName Logical identifier for the window
   * @returns Reference to the focused window or null if not found/accessible
   */
  private focusExistingTab(logicalName: string): Window | null {
    try {
      // Check all tabs from the library
      const allTabs = this.parent.getAllTabs()
      const tab = allTabs.find((t) => t && t.windowName === logicalName && t.status === 'open')

      // Check if we found it and if it has a reference
      if (tab && tab.ref) {
        try {
          tab.ref.focus()
          return tab.ref
        } catch {
          // If focus fails, the window is probably closed
          this.markTabAsClosed(logicalName)
          return null
        }
      }
    } catch (err) {
      console.error('[TabManager] Error focusing existing tab:', err)
    }

    // Tab not found or reference invalid
    return null
  }

  /**
   * Focuses an existing open tab matching the logicalName, or opens a new one if not found
   * @param logicalName Logical identifier for the window
   * @param url URL to open if tab doesn't exist
   * @returns Reference to the window or null if operation failed
   */
  public focusOrOpenChildTab(logicalName: string, url: string): Window | null {
    console.log(`[TabManager] Attempting focus/open for logicalName: ${logicalName}`)

    // Try to focus first
    const existingWindow = this.focusExistingTab(logicalName)
    if (existingWindow) {
      console.log(`[TabManager] Focusing existing open tab: ${logicalName}`)
      return existingWindow
    }

    // If focusing failed or tab not found, open a new one
    console.log(`[TabManager] No open tab found for ${logicalName}. Opening new.`)
    return this.openNewWindowWithDimensions(url, logicalName)
  }

  /**
   * Check if tab with given logical name exists
   * @param logicalName Logical identifier for the window
   */
  public hasTab(logicalName: string): boolean {
    return this.isTabOpen(logicalName)
  }

  /**
   * Close a specific tab by logical name
   * @param logicalName Logical identifier for the window
   */
  public closeTab(logicalName: string): void {
    const tabEntry = this.getTabByName(logicalName)
    if (tabEntry && tabEntry.id) {
      this.parent.closeTab(tabEntry.id)
      this.markTabAsClosed(logicalName)
    }
  }

  /**
   * Close all child tabs
   */
  public closeAllTabs(): void {
    this.parent.closeAllTabs()

    // Mark all tabs as closed in our registry
    this.tabRegistry.forEach((_, key) => {
      if (!(_.ref instanceof Window)) {
        const window = _.ref!.ref as Window | undefined
        window?.close()
      }

      this.markTabAsClosed(key)
    })
  }

  /**
   * Get all opened tabs
   * @returns Array of tab objects
   */
  public getAllTabs(): TabInfo[] {
    return this.parent.getAllTabs()
  }

  /**
   * Get tab by name
   * @param name Tab name to find
   * @returns Tab info if found, null otherwise
   */
  public getTabByName(name: string): TabInfo | null {
    const tabs = this.parent.getAllTabs()
    return tabs.find((tab) => tab.windowName === name) || null
  }

  public broadcastAll(message: string) {
    this.parent.broadCastAll({ type: 'PARENT_CHILD_COMMUNICATION', data: message })
  }
}

// Create a singleton instance
const tabManager = TabManager.getInstance()

// Export utility functions that use the singleton
export const openNewWindowWithDimensions = (url: string, logicalName: string) => {
  return tabManager.openNewWindowWithDimensions(url, logicalName)
}

export const focusOrOpenChildTab = (logicalName: string, url: string) => {
  return tabManager.focusOrOpenChildTab(logicalName, url)
}

export const getViewerLogicalName = (taskId: string, viewerIndex: number): string => {
  return `dicomViewer-${taskId}-${viewerIndex}`
}

export const registerTabCallback = (key: string, callback: (data: MessageData) => void): void => {
  tabManager.registerCallback(key, callback)
}

export const unregisterTabCallback = (key: string): void => {
  tabManager.unregisterCallback(key)
}

export const isTabOpen = (logicalName: string): boolean => {
  return tabManager.isTabOpen(logicalName)
}

export const closeTab = (logicalName: string): void => {
  tabManager.closeTab(logicalName)
}

export const closeAllTabs = (): void => {
  tabManager.closeAllTabs()
}

export const getAllTabs = (): TabInfo[] => {
  return tabManager.getAllTabs()
}

export const getTabByName = (name: string): TabInfo | null => {
  return tabManager.getTabByName(name)
}

export const broadCastAll = (message: string) => {
  tabManager.broadcastAll(message)
}

// Export for backward compatibility
export const parent = tabManager
